import './client-otp-modal.scss';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';
import { formatPhoneNumber } from '@/shared/services/formatter';

import { Button, Modal } from '../../ui';
import useClientForm from './hooks/useClientForm';
import useOtpForm from './hooks/useOtpForm';
import type { ClientFormValues } from './types/client-form';
import type { ModalOpenerType } from './types/general';
import type { OtpFormValues } from './types/otp-form';
import { ClientForm, Loader, OtpForm, Timer } from './ui';

interface IProps {
  title: string;
  subtitle?: string;
  onClientSubmit: (values: ClientFormValues) => Promise<boolean>;
  onOtpSubmit: (values: OtpFormValues) => Promise<string>;
  onSMSRetry: (phone: string) => void;
  defaultOpenedModal: ModalOpenerType;
  onClose: () => void;
  otpStepClientPrefill?: ClientFormValues | null;
  mainAgreements: {
    policyLink: string;
    agreementLink: string;
  };
}

const ClientOtpModal: React.FC<IProps> = ({
  title,
  subtitle,
  onClientSubmit,
  onOtpSubmit,
  defaultOpenedModal,
  onClose,
  onSMSRetry,
  otpStepClientPrefill = null,
  mainAgreements
}) => {
  const { t } = useTranslation();
  const [openedModal, setOpenedModal] = useState<ModalOpenerType>(0);

  const {
    clientFormMethods,
    clientFormLoading,
    clientError,
    handleClientFormSubmit
  } = useClientForm({
    submitRequest: onClientSubmit,
    handleNext: () => setOpenedModal(2)
  });
  const phone = clientFormMethods.watch('phone');

  const handleClose = () => {
    onClose();
    setOpenedModal(0);
    resetOtpError();
  };

  const {
    otpFormMethods,
    handleOtpFormSubmit,
    otpLoading,
    otpError,
    resetOtpError
  } = useOtpForm({
    submitRequest: onOtpSubmit,
    onClose: handleClose
  });

  useEffect(() => {
    if (defaultOpenedModal === 0) {
      setOpenedModal(0);
      return;
    }
    if (defaultOpenedModal === 2 && otpStepClientPrefill) {
      clientFormMethods.reset(otpStepClientPrefill);
      setOpenedModal(2);
      return;
    }
    setOpenedModal(defaultOpenedModal);
  }, [defaultOpenedModal, otpStepClientPrefill]);

  useEffect(() => {
    const skipClientResetWhileOtpHandoff =
      openedModal === 0 &&
      defaultOpenedModal === 2 &&
      otpStepClientPrefill != null;
    if (openedModal === 0 && !skipClientResetWhileOtpHandoff) {
      clientFormMethods.reset({
        personalId: '',
        fullName: '',
        phone: ''
      });
    }
    if (openedModal === 0 || openedModal === 1) {
      otpFormMethods.reset({
        first: undefined,
        second: undefined,
        third: undefined,
        fourth: undefined
      });
    }
  }, [openedModal, defaultOpenedModal, otpStepClientPrefill]);

  useEffect(() => {
    if (defaultOpenedModal === 1) {
      clientFormMethods.clearErrors(['personalId', 'fullName', 'phone']);
      setTimeout(() => clientFormMethods.setFocus('personalId'), 200);
    } else if (defaultOpenedModal === 2) {
      otpFormMethods.clearErrors(['first', 'second', 'third', 'fourth']);
      setTimeout(() => otpFormMethods.setFocus('first'), 200);
    }
  }, [defaultOpenedModal]);

  return (
    <>
      <Modal
        centered={true}
        className="client-otp-modal"
        title={title}
        open={openedModal === 1}
        onClose={handleClose}
        maskClosable={true}
        footer={<></>}
      >
        {subtitle && <p className="client-otp-modal__subtitle">{subtitle}</p>}

        <ClientForm
          methods={clientFormMethods}
          handleClose={handleClose}
          clientFormLoading={clientFormLoading}
          clientError={clientError}
          handleClientFormSubmit={handleClientFormSubmit}
          mainAgreements={mainAgreements}
        />
      </Modal>

      <Modal
        centered={true}
        className="client-otp-modal"
        title={t('enter_sms_code')}
        open={openedModal === 2}
        destroyOnHidden={true}
        onClose={handleClose}
        maskClosable={true}
        footer={<></>}
      >
        <p className="client-otp-modal__subtitle">{t('sms_sent_to')}</p>
        <p className="client-otp-modal__phone">
          {formatPhoneNumber(phone || '', { space: true })}
        </p>

        <div className="client-otp-modal__form">
          <OtpForm
            resetOtpError={resetOtpError}
            methods={otpFormMethods}
            onSubmit={handleOtpFormSubmit}
          />
        </div>
        {otpError && (
          <p className="client-otp-modal__error">
            <AlertCircleIcon />
            {otpError}
          </p>
        )}

        <div
          className={`client-otp-modal__otp-loading ${otpLoading ? 'client-otp-modal__otp-loading--active' : ''}`}
        >
          <Loader />
        </div>
        <div
          className={`client-otp-modal__timer ${!otpLoading ? 'client-otp-modal__timer--active' : ''}`}
        >
          <Timer
            timerStarted={openedModal === 2}
            onRetry={() => onSMSRetry(phone || '')}
          />
        </div>

        {!otpLoading && (
          <Button onClick={handleClose} className="client-otp-modal__cancel">
            {t('cancel')}
          </Button>
        )}
      </Modal>
    </>
  );
};

export default ClientOtpModal;
